/**
 * EventBus - Simple typed event emitter for component communication
 */

type EventCallback<T = unknown> = (data: T) => void;

export interface EventMap {
  // Connection events
  'connection:stateChange': { state: string; previousState: string; pc?: RTCPeerConnection };
  'connection:error': { error: Error; recoverable: boolean };
  'connection:reconnecting': { attempt: number; maxAttempts: number };
  'connection:switched': { type: string; stunServers: string[]; videoOnly: boolean; pc: RTCPeerConnection };

  // Signaling events
  'signaling:open': void;
  'signaling:close': { code: number; reason: string };
  'signaling:message': { type: string; data: unknown; dir: 'in' | 'out' };
  'signaling:error': Error;
  // 信令服务器进程是否在响应（HTTP 探测）。null=未探测；与 ws 状态独立用于 UI 区分"WS断"和"服务器挂"
  'signaling:server_reachable': { reachable: boolean };

  // WebRTC events
  'webrtc:offer': RTCSessionDescriptionInit;
  'webrtc:answer': RTCSessionDescriptionInit;
  'webrtc:candidate': RTCIceCandidateInit;
  'webrtc:track': MediaStreamTrack;
  'webrtc:datachannel': RTCDataChannel;
  'webrtc:iceState': RTCIceConnectionState;
  'webrtc:dataChannelReady': boolean;
  'webrtc:requestKeyframe': void;
  'webrtc:pcSwitched': { pc: RTCPeerConnection; stunServers: string[] };

  // Stats events
  'stats:update': { server: unknown; webrtc: unknown };
  'stats:warning': { type: string; message: string };
  'stats:sendJitterBuffer': { jitterBuffer: number };
  'stats:sendNetworkStats': { rtt: number; jitter: number; packetLoss: number; jitterBuffer: number; bitrate: number };
  'stats:serverStats': unknown;
  // PathObserver 专用质量数据源：full / 低功耗两个采集分支都发射（stats:update 仅 full 分支发，
  // 会被状态栏可见性门控——劣化检测不允许因 UI 隐藏失明，见 StatsService.emitPathQuality）
  'stats:pathQuality': { packetLoss: number; framesReceived: number; framesDecoded: number; keyframesDecoded: number };
  'stats:serverCaps': unknown;  // server_caps 小消息（承载 SettingsPanel 必需能力字段）
  'stats:encoderBitrate': { bitrate: number };  // 编码器目标码率 (bps)
  'stats:persistentPacketLoss': { packetLoss: number; duration: number };

  // 统一「视频卡死」事件（B0 中枢）：StatsService 单一检测器 → WebRTCService.handleVideoStuck 单一决策
  'video:stuck': {
    reason: 'stall-after-decode' | 'codec-undecodable' | 'first-frame-stall';
    neverDecoded: boolean;
    duration: number;
    framesReceived: number;
    currentCodec: string;
  };

  // Input events
  'input:enabled': { mouse: boolean; keyboard: boolean };
  'input:cursor': { cursor?: { cursorCSS?: string; visible?: boolean; cursorId?: number; imageData?: string; width?: number; height?: number; hotspotX?: number; hotspotY?: number } };
  'input:setRelativeMode': boolean;
  'input:keyboardLocked': boolean;  // Fullscreen keyboard lock state for system key capture
  'input:forceShowCursor': boolean; // Force show cursor (ESC long press)
  'input:gameMouseMode': boolean;   // Game mouse mode toggle (Alt+F1 to exit)

  // Mouse channel event
  'webrtc:mouseChannel': RTCDataChannel;

  // File manager JSON-RPC channel event
  'webrtc:fsRpcChannel': RTCDataChannel;
  // 请求打开 NewWeb 文件管理器窗口（搜索结果「打开所在文件夹」/ 初始化中查看进度等触发）
  'fm:open': void;
  // 请求关闭 NewWeb 文件管理器窗口（远程定位/远程打开成功后触发）
  'fm:close': void;

  // Clipboard events
  'clipboard:sync': string;
  // 拉模式：Ctrl+C 后查询命中文件 / 用户手动拉取触发
  'clipboard:filesAvailable': { files: unknown[]; totalSize: number; fileCount: number };

  // Audio events
  'audio:systemTrack': MediaStream;
  'audio:micTrack': MediaStream;

  // Status bar events
  'statusBar:hidden': object;

  // UI events
  'ui:toast': { message: string; type: string };
  'ui:log': { level: string; message: string };
  // P2P Assist 升级确认弹窗
  'ui:showP2PAssistDialog': {
    sessionId: string;
    stunServers: string[];
    rttCurrentMs?: number;     // 当前 relay 连接的 RTT（可选）
    rttP2PMs?: number;         // P2P standby 探测到的 RTT（可选）
    countdownSec: number;      // 倒计时秒数
  };

  // Agent events
  'agent:restart': { message: string };

  // Peer/Relay events
  'peer:status': { status: string; peerType: string };
  'relay:peerTimeout': void;
  'peers:update': { peers: Array<{ clientId: string; ip: string; fingerprint: string; joinTime: number }>; total: number };

  // Host events
  'host:offline': void;
  // 重连周期内每次尝试时触发；reason 区分应用层"host_offline"（host 没在线）与网络层失败（ws-close / connect-timeout / host-offline 之外的所有原因），UI 据此走不同覆盖文案
  'host:waiting': { attempt: number; maxAttempts: number; reason: string };
  'host:crashInfo': { content: string };
  'room:full': void;

  // Capture events (MSTSC minimized, etc.)
  'capture:accessDenied': { message: string };    // Remote desktop minimized or access lost
  'capture:accessRestored': { message: string };  // Access restored after being denied

  // Idle detection events
  'idle:stateChange': { state: 'active' | 'warning' | 'paused'; countdown?: number };

  // Auth events
  'auth:unauthorized': void;  // 401 兜底刷新失败 / URL 无 token 且本地无 token

  // File search events
  'file_search:status': { status: string; progress?: number; current?: number; total?: number; phase?: string; message?: string };
  'file_search:openSearchHotkey': void;  // Alt+F 请求打开全局文件搜索

  // PathObserver / PathMigration 事件（docs/path_observer_design.md §4.4）
  // p2p_ready: P2PAssist 打洞成功上报（决策收权后由 PathObserver 扣扳机）
  'path:p2p_ready': { sessionId: string };
  // 链路劣化倒计时提示：质量差开窗(start)→恢复取消(recovered)/熬满切换(switching)/被拦撤下(dismiss)
  'path:degrade': { phase: 'start' | 'recovered' | 'switching' | 'dismiss'; label?: string; windowMs?: number; startTs?: number };
  // path_migrate 消息族 host → 前端回执（offer/candidate 走现有 webrtcviewer+raceId 通道，不在此列）
  'path_migrate:switch_ready': { sessionId: string };
  'path_migrate:error': { sessionId: string; reason: string };

  // P2P Assist events
  'p2p_assist:answer': { sessionId?: string; sdp?: string };
  'p2p_assist:candidate': { sessionId?: string; candidate?: RTCIceCandidateInit };
  'p2p_assist:step_update': { sessionId?: string; step?: number; stepName?: string; status?: string; error?: string; details?: string };
  'p2p_assist:quality_progress': { sessionId?: string; chunkIndex?: number; totalChunks?: number; totalBytes?: number };
  'p2p_assist:renegotiate_offer': { raceId: string; sdp: string };
  'p2p_assist:switch_ready': { sessionId?: string };
  'p2p_assist:performSwitch': { sessionId: string; pc: RTCPeerConnection; dataChannel: RTCDataChannel | null; inputChannel: RTCDataChannel | null; mouseChannel: RTCDataChannel | null; remoteStream: MediaStream; stunServers: string[]; connectionType: string };

  // Config events
  'config:icePolicyChange': string;  // 'all' | 'p2p' | 'relay'
  'config:autoReset': void;  // 系统自动切换后重置 icePolicy+turnProtocol 为 auto

  // 内网 TURN 状态推送（host → 前端）
  // payload 字段对应 types.ts 的 InternalTurnStatus
  'internal_turn:status': {
    enabled: boolean;
    discoveryComplete: boolean;
    errorReason: string;
    endpoints: Array<{
      ip: string;
      port: number;
      protocols: string[];
      reachable: boolean;
      rttMs: number;
      lastProbeAt: number;
    }>;
  };

  // ICE Racing events
  'race:offer': { raceId: string; offer: RTCSessionDescriptionInit };
  'race:candidate': { raceId: string; candidate: RTCIceCandidateInit };
  'race:winner': {
    pc: RTCPeerConnection;
    dataChannel: RTCDataChannel | null;
    mouseChannel: RTCDataChannel | null;
    fsRpcChannel: RTCDataChannel | null;
    fileCtrlChannel: RTCDataChannel | null;
    fileDataChannel: RTCDataChannel | null;
    remoteStream: MediaStream | null;
    raceId: string;
    connectionType: 'p2p' | 'relay';
    connectTimeMs: number;
    iceServers: RTCIceServer[];
    turnProtocol?: 'TCP' | 'UDP';
  };
  'race:failed': { reason: string };
  'race:cancelled': void;
  'race:status': {
    elapsedMs: number;
    phase?: 'connecting' | 'testing' | 'completed';
    sessions: Array<{
      raceId: string;
      label: string;
      status: 'pending' | 'offer_received' | 'racing' | 'testing' | 'connected' | 'failed' | 'cancelled';
      iceState?: string;
      connectionType?: 'p2p' | 'relay';
      turnServer?: string;
      connectTime?: number;
      isWinner?: boolean;
      remoteCandidateCount?: number;
      quality?: {
        packetsReceived: number;
        packetsLost: number;
        throughputKbps: number;
        score: number;
        rtt?: number;
        rttJitterMs?: number;
        stableThroughputKbps?: number;
      };
    }>;
    winner?: string;
    winReason?: 'fast-win' | 'quality-select' | 'timeout-fallback' | 'forceRelay';
    thresholds?: {
      minThroughputKbps: number;
      maxLossPct: number;
      maxJitterMs: number;
    };
  };
  'race:host_status': {
    elapsedMs: number;
    sessions: Array<{
      raceId: string;
      label: string;
      status: string;
      iceState?: string;
      connectionType?: string;
      turnServer?: string;
      actualTurnEntry?: string; // 本 session 实际选中的内网入口"ip:port"（内网模式 relay）
      connectTime?: number;
      isWinner?: boolean;
      remoteCandidateCount?: number;
      quality?: {
        packetsReceived: number;
        packetsLost: number;
        throughputKbps: number;
        packetsSent?: number;
        score?: number;
        rtt?: number;
      };
    }>;
    winner?: string;
    error?: string; // host 侧硬错误（如 internal_turn_unavailable），race 不可能成功
  };

  // File transfer events
  'fileTransfer:ready': boolean;
  'fileTransfer:update': {
    id: string;
    fileName: string;
    totalFiles: number;
    totalSize: number;
    transferredBytes: number;
    status: string;
    startTime: number;
    files: Array<{
      id: number;
      name: string;
      path: string;
      size: number;
      transferredBytes: number;
      status: 'pending' | 'transferring' | 'completed' | 'failed';
    }>;
  };
}

class EventBus {
  private listeners: Map<string, Set<EventCallback>> = new Map();
  private onceListeners: Map<string, Set<EventCallback>> = new Map();

  /**
   * Subscribe to an event
   */
  on<K extends keyof EventMap>(event: K, callback: EventCallback<EventMap[K]>): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback as EventCallback);

    // Return unsubscribe function
    return () => this.off(event, callback);
  }

  /**
   * Subscribe to an event once
   */
  once<K extends keyof EventMap>(event: K, callback: EventCallback<EventMap[K]>): () => void {
    if (!this.onceListeners.has(event)) {
      this.onceListeners.set(event, new Set());
    }
    this.onceListeners.get(event)!.add(callback as EventCallback);

    return () => {
      this.onceListeners.get(event)?.delete(callback as EventCallback);
    };
  }

  /**
   * Unsubscribe from an event
   */
  off<K extends keyof EventMap>(event: K, callback: EventCallback<EventMap[K]>): void {
    this.listeners.get(event)?.delete(callback as EventCallback);
    this.onceListeners.get(event)?.delete(callback as EventCallback);
  }

  /**
   * Emit an event
   */
  emit<K extends keyof EventMap>(event: K, data: EventMap[K]): void {
    // Regular listeners
    this.listeners.get(event)?.forEach((callback) => {
      try {
        callback(data);
      } catch (e) {
        console.error(`[EventBus] Error in ${event}:`, e);
      }
    });

    // Once listeners
    const onceCallbacks = this.onceListeners.get(event);
    if (onceCallbacks) {
      onceCallbacks.forEach((callback) => {
        try {
          callback(data);
        } catch (e) {
          console.error(`[EventBus] Error in ${event}:`, e);
        }
      });
      onceCallbacks.clear();
    }
  }

  /**
   * Remove all listeners for an event or all events
   */
  clear(event?: keyof EventMap): void {
    if (event) {
      this.listeners.delete(event);
      this.onceListeners.delete(event);
    } else {
      this.listeners.clear();
      this.onceListeners.clear();
    }
  }

  /**
   * Get listener count for debugging
   */
  listenerCount(event: keyof EventMap): number {
    return (this.listeners.get(event)?.size || 0) + (this.onceListeners.get(event)?.size || 0);
  }
}

// Singleton instance
export const eventBus = new EventBus();
export default eventBus;
